<template>
  <div class="form list-filters">
    <template
      v-for="filter in filterFields"
      :key="filter.key"
    >
      <AppTextField
        v-if="filter.type === 'search'"
        class="list-filters__control list-filters__control--search"
        :class="{ 'list-filters__control--grow': filter.grow }"
        :model-value="selectedFilters[filter.key]"
        :placeholder="filter.placeholder"
        prepend-inner-icon="mdi-magnify"
        :width="filter.width"
        :density="filterControlDensity"
        @update:model-value="setFilter(filter, $event)"
      />

      <AppDateRangeField
        v-else-if="filter.type === 'dateRange'"
        class="list-filters__control list-filters__control--date"
        :model-value="datePickerValues[filter.key] || []"
        :placeholder="filter.placeholder"
        :width="filter.width"
        :density="filterControlDensity"
        @update:model-value="setFilter(filter, $event)"
      />

      <AppSelect
        v-else-if="filter.type === 'select'"
        class="list-filters__control"
        appearance="filter"
        clearable
        :items="filter.items"
        :model-value="selectedFilters[filter.key]"
        :placeholder="filter.placeholder"
        :width="filter.width"
        :density="filterControlDensity"
        @update:model-value="setFilter(filter, $event)"
      />
    </template>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'

import AppDateRangeField from './AppDateRangeField.vue'
import AppSelect from './AppSelect.vue'
import AppTextField from './AppTextField.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },

  filterFields: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue', 'change'])

const selectedFilters = ref({})
const datePickerValues = ref({})
const searchTimers = ref({})
const filterControlDensity = 'comfortable'

watch(
  () => props.modelValue,
  (filters) => {
    selectedFilters.value = { ...filters }
    datePickerValues.value = props.filterFields.reduce((values, filter) => {
      const dateRange = filters[filter.key]

      if (filter.type === 'dateRange' && dateRange?.from) {
        values[filter.key] = [
          parseApiDate(dateRange.from),
          ...(dateRange.to ? [parseApiDate(dateRange.to)] : []),
        ]
      }

      return values
    }, {})
  },
  {
    deep: true,
    immediate: true,
  }
)

onBeforeUnmount(() => {
  Object.values(searchTimers.value).forEach((timer) => clearTimeout(timer))
})

function setFilter (filter, value) {
  let filterValue = filter.type === 'search' && value === '' ? null : value

  if (filter.type === 'dateRange') {
    const dates = value
    const from = dates[0]
    const to = dates.length > 1 ? dates[dates.length - 1] : null

    datePickerValues.value[filter.key] = dates
    filterValue = from ? { from: formatApiDate(from), to: to ? formatApiDate(to) : null } : null
  }

  const filters = props.filterFields.reduce((values, currentFilter) => {
    values[currentFilter.key] = currentFilter.key === filter.key
      ? filterValue
      : selectedFilters.value[currentFilter.key] ?? null
    return values
  }, {})

  selectedFilters.value = filters

  if (filter.type === 'search') {
    clearTimeout(searchTimers.value[filter.key])
    searchTimers.value[filter.key] = setTimeout(() => {
      emit('update:modelValue', selectedFilters.value)
      emit('change', selectedFilters.value)
    }, 500)
    return
  }

  emit('update:modelValue', filters)
  emit('change', filters)
}

function formatApiDate (date) {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

function parseApiDate (date) {
  const [year, month, day] = date.split('-').map(Number)

  return new Date(year, month - 1, day)
}
</script>

<style
  scoped
  lang="scss"
>
.list-filters {
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  gap: clamp-bp("sm", "xl", 12, 21);
  width: 100%;
  background: transparent;

  @include breakpoint-up("sm") {
    align-items: center;
    background: var(--color-white);
  }

  &__control {
    flex-basis: 100%;
    width: 100%;
    max-width: none;
    min-width: 0;

    @include breakpoint-up("sm") {
      flex: 1 1 var(--form-control-width);
      width: auto;
      max-width: var(--form-control-width);
      min-width: 180px;
    }

    &--grow {
      @include breakpoint-up("sm") {
        flex: 1 1 0;
        max-width: none;
      }
    }

    &--search,
    &--date {
      :deep(.v-field) {
        background: var(--color-white);
        box-shadow: var(--shadow-control);
      }

      :deep(.v-field__outline) {
        --v-field-border-width: 1px;
        --v-field-border-opacity: 1;
        color: var(--color-border);
      }

      :deep(.v-field__input) {
        padding-top: 0;
        padding-bottom: 0;
        color: var(--color-text-placeholder);
        font-size: 16px;
        font-weight: 300;
      }

      :deep(.v-field__prepend-inner),
      :deep(.v-field__append-inner) {
        align-items: center;
        padding-top: 0;
      }

      :deep(.v-icon) {
        color: var(--color-text-placeholder);
        font-size: clamp-bp("sm", "xl", 18, 20);
        opacity: 1;
      }

      :deep(input.v-field__input),
      :deep(input.v-field__input::placeholder) {
        color: var(--color-text-placeholder);
        -webkit-text-fill-color: var(--color-text-placeholder);
        opacity: 1;
      }

      :deep(input.v-field__input::placeholder) {
        font-size: var(--placeholder-font-size);
      }
    }
  }
}
</style>
